/**
 * EvaluationScoreComparisonSection — "مقارنة أداء الجهات وفق نتائج التقييم"
 *
 * Sibling of EvaluationComparisonSection (the blended "وفق التقييم" chart),
 * placed directly ABOVE it on the executive dashboard. Plots the raw approved
 * evaluator `overall_score` per department — straight from the evaluation
 * results, with NO commitment blend:
 *
 *   bar value = EntityEvaluation.overall_score   (0–100, approved only)
 *
 * Data is the `evaluator_score` field of the shared blend-comparison payload —
 * the same payload that feeds the sibling blend chart, so EVERY department in
 * the SSO roster renders (score 0 when it has no approved evaluation yet), not
 * just the handful with approved evals. Bars + legend reuse the same
 * red/orange/green scheme and design as the two sibling charts
 * (EvaluationComparisonSection / OrganizationsComparisonSection). The entity
 * picker filters client-side.
 *
 * Threshold note: bands mirror the blend chart (0–10 red, 11–29 orange,
 * 30–100 green) so the design is pixel-identical to its sibling. Adjust the two
 * helpers below if evaluation-score bands should differ from the blend bands.
 */

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Modal,
  Pressable,
  ScrollView,
} from 'react-native';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts';

import { tokens } from '@design';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTextAlign } from '../utils/rtl';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { FONTS } from '../constants/config';
import { METRIC_RED, METRIC_ORANGE, METRIC_GREEN } from '../utils/metricColors';
import type { BlendComparisonEntity } from '../services/evaluationApi';

interface EvaluationScoreComparisonSectionProps {
  data: BlendComparisonEntity[];
  loading?: boolean;
  fromDate?: string | null;
  toDate?: string | null;
}

// Bars reuse the red/orange/green scheme of the sibling evaluation chart, with
// the same thresholds on the score: 0–10 → red, 11–29 → orange, 30–100 → green.
const scoreBarColor = (score: number): string => {
  if (score >= 30) return METRIC_GREEN;
  if (score >= 11) return METRIC_ORANGE;
  return METRIC_RED;
};

// i18n key for the bar's status bucket (matches scoreBarColor thresholds).
const scoreStatusKey = (score: number): string => {
  if (score >= 30) return 'charts.excellent';
  if (score >= 11) return 'charts.moderate';
  return 'charts.poor';
};

// recharts margin (chart geometry, not an RTL style prop)
// eslint-disable-next-line no-restricted-syntax
const BAR_CHART_MARGIN = { top: 20, right: 20, left: 20, bottom: 5 };

// Wrap long Arabic department names onto multiple SVG lines (recharts clips the
// default single-line tick). Mirrors the sibling comparison charts.
const WrappedAxisTick = (props: any) => {
  const { x, y, payload, fontSize = 11, fill, fontFamily, maxCharsPerLine = 14, maxLines = 4, lineHeight = 13 } = props;
  const value: string = (payload && payload.value) || '';

  const words = value.split(' ').filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxCharsPerLine) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      if (word.length > maxCharsPerLine) {
        for (let i = 0; i < word.length; i += maxCharsPerLine) {
          lines.push(word.slice(i, i + maxCharsPerLine));
        }
        current = '';
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  const visible = lines.slice(0, maxLines);

  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fill={fill} fontSize={fontSize} fontFamily={fontFamily}>
        {visible.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? fontSize : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

// Hover shows the evaluation score and its status band.
const CustomChartTooltip = ({ active, payload, scoreLabel, levelLabel }: any) => {
  if (active && payload && payload.length) {
    const row = payload[0].payload;
    return (
      <View style={chartTooltipStyles.container}>
        <Text style={chartTooltipStyles.title}>{row.fullName}</Text>
        <View style={chartTooltipStyles.row}>
          <View style={[chartTooltipStyles.dot, { backgroundColor: row.color }]} />
          <Text style={chartTooltipStyles.label}>{scoreLabel}</Text>
          <Text style={chartTooltipStyles.value}>%{row.overall_score}</Text>
        </View>
        {row.level_label ? (
          <View style={chartTooltipStyles.row}>
            <Text style={chartTooltipStyles.label}>{levelLabel}</Text>
            <Text style={chartTooltipStyles.value}>{row.level_label}</Text>
          </View>
        ) : null}
      </View>
    );
  }
  return null;
};

// Static status legend matching the red/orange/green bar thresholds — mirrors
// the sibling evaluation chart (EvaluationComparisonSection).
const CustomLegend = ({ poorLabel, moderateLabel, excellentLabel }: { poorLabel: string; moderateLabel: string; excellentLabel: string }) => (
  <View style={styles.legend}>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_RED }]} />
      <Text style={styles.legendText}>{poorLabel}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_ORANGE }]} />
      <Text style={styles.legendText}>{moderateLabel}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_GREEN }]} />
      <Text style={styles.legendText}>{excellentLabel}</Text>
    </View>
  </View>
);

export const EvaluationScoreComparisonSection: React.FC<EvaluationScoreComparisonSectionProps> = ({
  data,
  loading,
  fromDate: fromDateProp,
  toDate: toDateProp,
}) => {
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translateOrg } = useAutoTranslate();

  const today = new Date();
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [fromDate, setFromDate] = useState<string>(fromDateProp ?? toIso(yearStart));
  const [toDate, setToDate] = useState<string>(toDateProp ?? toIso(today));

  const [selectedDeptId, setSelectedDeptId] = useState<number | null>(null);
  const [showDeptPicker, setShowDeptPicker] = useState(false);

  const themeColors = {
    sectionTitle: colors.textPrimary,
    chartCard: colors.card,
    axisText: colors.textMuted,
    emptyText: colors.textMuted,
    gridColor: isDark ? '#4B5563' : '#E5E5E5',
    tooltipBg: colors.card,
    tooltipBorder: colors.cardBorder,
  };

  // Client-side filter: narrow by entity. The evaluator score is a point-in-
  // time approved result with no per-row date, so the date inputs (kept for
  // design parity with the sibling charts) don't gate the rows.
  const filteredData = useMemo(
    () => data.filter((d) => selectedDeptId == null || d.department_id === selectedDeptId),
    [data, selectedDeptId],
  );

  const chartData = useMemo(
    () => filteredData.map((d) => {
      const score = Number(Number(d.evaluator_score ?? 0).toFixed(2));
      return {
        name: translateOrg(d.department_name),
        fullName: translateOrg(d.department_name),
        overall_score: score,
        color: scoreBarColor(score),
        level_label: t(scoreStatusKey(score)),
      };
    }),
    [filteredData, translateOrg, t],
  );

  const selectedDeptLabel = selectedDeptId == null
    ? t('organizations.allOrganizations')
    : translateOrg(data.find((d) => d.department_id === selectedDeptId)?.department_name);

  const chartHeight = 440;

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparisonByEvaluationResults')}</Text>
        <View style={[styles.stateContainer, { backgroundColor: themeColors.chartCard }]}>
          <Text style={[styles.stateText, { color: themeColors.emptyText }]}>{t('common.loading')}</Text>
        </View>
      </View>
    );
  }

  if (data.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparisonByEvaluationResults')}</Text>
        <View style={[styles.stateContainer, { backgroundColor: themeColors.chartCard }]}>
          <Text style={[styles.stateText, { color: themeColors.emptyText }]}>{t('common.noData')}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparisonByEvaluationResults')}</Text>

      {/* Filters Row — mirrors the sibling evaluation chart */}
      <View style={styles.filtersRow}>
        {/* إلى تاريخ */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.toDate')}</Text>
          {Platform.OS === 'web' ? (
            React.createElement('input', {
              type: 'date',
              value: toDate,
              min: fromDate,
              onChange: (e: any) => setToDate(e.target.value),
              onClick: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              style: {
                width: '100%', height: 32, paddingInline: 8,
                borderRadius: tokens.radii.absolute['radius-8'],
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: themeColors.chartCard, color: themeColors.sectionTitle,
                fontFamily: FONTS.regular,
                fontSize: tokens.typography.paragraph['paragraph mini font size'],
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)', outline: 'none',
                direction: 'rtl', cursor: 'pointer',
              },
            })
          ) : (
            <View style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}>
              <Text style={[styles.filterValue, { color: themeColors.sectionTitle }]}>{toDate}</Text>
            </View>
          )}
        </View>

        {/* من تاريخ */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.fromDate')}</Text>
          {Platform.OS === 'web' ? (
            React.createElement('input', {
              type: 'date',
              value: fromDate,
              max: toDate,
              onChange: (e: any) => setFromDate(e.target.value),
              onClick: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              style: {
                width: '100%', height: 32, paddingInline: 8,
                borderRadius: tokens.radii.absolute['radius-8'],
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: themeColors.chartCard, color: themeColors.sectionTitle,
                fontFamily: FONTS.regular,
                fontSize: tokens.typography.paragraph['paragraph mini font size'],
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)', outline: 'none',
                direction: 'rtl', cursor: 'pointer',
              },
            })
          ) : (
            <View style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}>
              <Text style={[styles.filterValue, { color: themeColors.sectionTitle }]}>{fromDate}</Text>
            </View>
          )}
        </View>

        {/* الجهات */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.organizations')}</Text>
          <TouchableOpacity
            style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}
            onPress={() => setShowDeptPicker(true)}
            activeOpacity={0.7}
          >
            <Text style={[styles.filterIcon, { color: themeColors.axisText }]}>⌄</Text>
            <Text
              style={[styles.filterValue, { color: selectedDeptId == null ? themeColors.axisText : themeColors.sectionTitle }]}
              numberOfLines={1}
            >
              {selectedDeptLabel}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Department Picker Modal */}
      <Modal visible={showDeptPicker} transparent animationType="fade" onRequestClose={() => setShowDeptPicker(false)}>
        <Pressable style={modalStyles.backdrop} onPress={() => setShowDeptPicker(false)}>
          <Pressable
            style={[orgPickerStyles.container, { backgroundColor: themeColors.tooltipBg, borderColor: themeColors.tooltipBorder }]}
            onPress={(e) => e.stopPropagation()}
          >
            <ScrollView style={{ maxHeight: 320 }}>
              <TouchableOpacity style={orgPickerStyles.row} onPress={() => { setSelectedDeptId(null); setShowDeptPicker(false); }}>
                <Text style={[orgPickerStyles.rowText, selectedDeptId == null && orgPickerStyles.rowTextActive, { color: themeColors.sectionTitle }]}>
                  {t('organizations.allOrganizations')}
                </Text>
              </TouchableOpacity>
              {data.map((d) => (
                <TouchableOpacity key={d.department_id} style={orgPickerStyles.row} onPress={() => { setSelectedDeptId(d.department_id); setShowDeptPicker(false); }}>
                  <Text
                    style={[orgPickerStyles.rowText, selectedDeptId === d.department_id && orgPickerStyles.rowTextActive, { color: themeColors.sectionTitle }]}
                    numberOfLines={1}
                  >
                    {translateOrg(d.department_name)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Chart Card */}
      <View style={[styles.chartCard, { backgroundColor: themeColors.chartCard }]}>
        {Platform.OS === 'web' && chartData.length > 0 ? (
          <View style={{ height: chartHeight, width: '100%', minWidth: 300, minHeight: chartHeight }}>
            <ResponsiveContainer width="100%" height={chartHeight} debounce={50}>
              <BarChart data={chartData} margin={BAR_CHART_MARGIN}>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={themeColors.gridColor} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  interval={0}
                  height={75}
                  tick={<WrappedAxisTick fontSize={11} fill={themeColors.axisText} fontFamily={FONTS.regular} />}
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: tokens.typography.paragraph['paragraph caption font size'], fill: themeColors.axisText, fontFamily: FONTS.regular }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip
                  content={(
                    <CustomChartTooltip
                      scoreLabel={t('organizations.evaluationScore')}
                      levelLabel={t('organizations.level')}
                    />
                  )}
                  cursor={{ fill: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }}
                />
                <Legend content={(
                  <CustomLegend
                    poorLabel={t('charts.poor')}
                    moderateLabel={t('charts.moderate')}
                    excellentLabel={t('charts.excellent')}
                  />
                )} />
                <Bar dataKey="overall_score" radius={[2, 2, 0, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </View>
        ) : (
          <View style={[styles.stateContainer, { backgroundColor: themeColors.chartCard }]}>
            <Text style={[styles.stateText, { color: themeColors.emptyText }]}>{t('common.chartWebOnly')}</Text>
          </View>
        )}
      </View>
    </View>
  );
};

export default EvaluationScoreComparisonSection;

const getStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    marginTop: tokens.spacing.absolute['6'],
    marginBottom: tokens.spacing.absolute['4'],
  },
  sectionTitle: {
    fontSize: tokens.typography.paragraph['paragraph subheading font size'],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute['2'],
    fontFamily: FONTS.medium,
  },
  filtersRow: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute['5'],
    marginBottom: tokens.spacing.absolute['4'],
    width: '100%',
  },
  filterField: {
    flex: 1,
    gap: tokens.spacing.absolute['1_5'],
  },
  filterLabel: {
    fontSize: tokens.typography.paragraph['paragraph small font size'],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  filterInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute['1_5'],
    height: 32,
    paddingHorizontal: tokens.spacing.absolute['1_5'],
    borderWidth: tokens.spacing.absolute['px'],
    borderRadius: tokens.radii.absolute['radius-8'],
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  },
  filterIcon: {
    fontSize: tokens.typography.paragraph['paragraph small font size'],
  },
  filterValue: {
    flex: 1,
    fontSize: tokens.typography.paragraph['paragraph mini font size'],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  chartCard: {
    borderRadius: tokens.radii.absolute['radius-12'],
    padding: tokens.spacing.absolute['4'],
    minWidth: 300,
    minHeight: 350,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
    elevation: 2,
  },
  legend: {
    flexDirection: 'row-reverse',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: tokens.spacing.absolute['6'],
    marginTop: tokens.spacing.absolute['4'],
    paddingTop: tokens.spacing.absolute['3'],
    borderTopWidth: tokens.spacing.absolute['px'],
    borderTopColor: '#E5E5E5',
  },
  legendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute['2'],
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: tokens.radii.absolute['radius-2'],
  },
  legendText: {
    fontSize: tokens.typography.paragraph['paragraph mini font size'],
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  stateContainer: {
    borderRadius: tokens.radii.absolute['radius-12'],
    padding: tokens.spacing.absolute['10'],
    alignItems: 'center',
  },
  stateText: {
    fontSize: tokens.typography.paragraph['paragraph small font size'],
    fontFamily: FONTS.regular,
  },
});

const getChartTooltipStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute['radius-8'],
    padding: tokens.spacing.absolute['3'],
    borderWidth: tokens.spacing.absolute['px'],
    borderColor: '#E5E5E5',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: 180,
  },
  title: {
    fontSize: tokens.typography.paragraph['paragraph mini font size'],
    fontWeight: '500',
    color: '#525252',
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute['2'],
    fontFamily: FONTS.regular,
  },
  row: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute['2'],
    marginBottom: tokens.spacing.absolute['1'],
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: tokens.radii.absolute['radius-2'],
  },
  label: {
    fontSize: tokens.typography.paragraph['paragraph mini font size'],
    color: '#525252',
    flex: 1,
    textAlign: getTextAlign(isRTL),
    fontFamily: FONTS.regular,
  },
  value: {
    fontSize: tokens.typography.paragraph['paragraph mini font size'],
    fontWeight: '600',
    color: '#171717',
    fontFamily: FONTS.medium,
  },
});

const getOrgPickerStyles = (isRTL: boolean) => StyleSheet.create({
  container: {
    width: 320,
    maxWidth: '90%' as const,
    borderRadius: tokens.radii.absolute['radius-8'],
    borderWidth: tokens.spacing.absolute['px'],
    paddingVertical: tokens.spacing.absolute['1_5'],
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 8,
  },
  row: {
    paddingHorizontal: tokens.spacing.absolute['4'],
    paddingVertical: tokens.spacing.absolute['2_5'],
  },
  rowText: {
    fontSize: tokens.typography.paragraph['paragraph small font size'],
    fontFamily: FONTS.regular,
    textAlign: getTextAlign(isRTL),
  },
  rowTextActive: {
    fontFamily: FONTS.medium,
  },
});

const getModalStyles = () => StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const styles = getStyles(true);
const chartTooltipStyles = getChartTooltipStyles(true);
const orgPickerStyles = getOrgPickerStyles(true);
const modalStyles = getModalStyles();
